import { MODULES_BY_KEY, type ModuleKey } from '@ruby/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Search } from 'lucide-react';
import { useEffect } from 'react';
import { MODULE_UI } from '@/app/modules';
import { Button } from '@/components/ui/button';
import { EmptyState, Kbd } from '@/components/ui/misc';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { spot } from '@/lib/spot';
import { Brand } from '../shell/brand';
import { CommandPalette, useCommandPalette } from '../shell/command-palette';
import { getLastModule } from '../shell/last-module';
import { InboxButton } from '../platform/inbox-button';
import { NotificationsButton } from '../platform/notifications-button';
import { UserMenu } from '../shell/user-menu';

function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Dubai' }).format(new Date()));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

const today = () => new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: '2-digit', month: 'short', timeZone: 'Asia/Dubai' }).format(new Date());

/** Landing screen after login: one tile per permitted module, no sidebar. */
export function ModuleSelection() {
  const boot = useSession((s) => s.boot)!;
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const modules = boot.modules.map((k) => MODULES_BY_KEY[k]).sort((a, b) => a.order - b.order);
  const last = getLastModule();

  // Number keys 1–9 open the corresponding tile.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || (e.target as HTMLElement).closest('input,textarea,select,[role=dialog]')) return;
      const m = modules[Number(e.key) - 1];
      if (m) void navigate({ to: '/m/$module', params: { module: m.key } });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modules, navigate]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-surface/75 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Brand subtitle="Enterprise Suite" />
          <div className="flex items-center gap-3">
            <Button variant="secondary" className="hidden h-10 w-64 justify-start rounded-xl bg-bg text-fg-subtle shadow-none sm:inline-flex" onClick={palette.open}>
              <Search /> Find a screen… <span className="ml-auto"><Kbd>Ctrl K</Kbd></span>
            </Button>
            <NotificationsButton />
            <InboxButton />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <section className="fx-rise relative flex flex-wrap items-center gap-5 overflow-hidden rounded-[22px] shadow-[0_18px_40px_-18px_rgb(15_81_50/0.55)] bg-[linear-gradient(135deg,#0f5132_0%,#14704a_60%,#1f8a5b_100%)] px-7 py-7 text-white">
          <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth=".6" aria-hidden className="pointer-events-none absolute -right-10 -bottom-16 size-72">
            <path d="M12 22V12M12 12C12 7 8 5 4 5c0 5 3 7 8 7zM12 12c0-4 3-7 8-7 0 5-3 7-8 7z" />
          </svg>
          <div className="relative">
            <p className="text-[13px] font-semibold text-emerald-200">
              {boot.user.branch?.name ?? boot.company.legalName} · {today()}
            </p>
            <h1 className="mt-1.5 text-[28px] font-bold tracking-tight">
              {greeting()}, {boot.user.fullName.split(' ')[0]}
            </h1>
            <p className="mt-1 text-emerald-100">Choose a module to continue.</p>
          </div>
          <div className="relative ml-auto flex gap-3">
            <div className="rounded-2xl bg-white/15 px-4 py-3">
              <p className="text-xs text-emerald-100">Modules available</p>
              <p className="tabular text-2xl font-extrabold">{modules.length}</p>
            </div>
            <div className="rounded-2xl bg-white/15 px-4 py-3">
              <p className="text-xs text-emerald-100">Signed in as</p>
              <p className="text-[15px] font-bold">{boot.user.roles[0]?.name ?? boot.user.username}</p>
            </div>
          </div>
        </section>

        {modules.length === 0 ? (
          <EmptyState title="No modules assigned" description="Your account does not have access to any module yet. Contact your administrator." />
        ) : (
          <div className="fx-stagger mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {modules.map((m, i) => {
              const ui = MODULE_UI[m.key as ModuleKey];
              const Icon = ui.icon;
              const isLast = last === m.key;
              return (
                <Link
                  key={m.key}
                  to="/m/$module"
                  params={{ module: m.key }}
                  onPointerMove={spot}
                  className={cn(
                    'fx-spot group relative flex min-h-[170px] flex-col gap-4 overflow-hidden rounded-[20px] border bg-surface p-6 shadow-(--shadow-card) transition-all duration-300',
                    'hover:-translate-y-1 hover:border-accent/50 hover:shadow-(--shadow-lift) focus-visible:border-accent',
                    // A soft glow follows into the corner on hover.
                    'before:pointer-events-none before:absolute before:-top-16 before:-right-16 before:size-40 before:rounded-full before:bg-accent/0 before:blur-2xl before:transition-colors before:duration-500 hover:before:bg-accent/15',
                    isLast ? 'border-accent ring-3 ring-primary-soft' : 'border-border',
                  )}
                >
                  <div className="flex items-start justify-between">
                    <span className={cn('flex size-[54px] items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6', ui.accent)}>
                      <Icon className="size-6" strokeWidth={1.8} />
                    </span>
                    {isLast ? (
                      <span className="rounded-full bg-primary-soft px-2.5 py-1 text-[11px] font-bold text-primary">Last used</span>
                    ) : (
                      i < 9 && <span className="hidden sm:inline"><Kbd>{i + 1}</Kbd></span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-[17px] font-bold">{m.name}</h2>
                    <p className="mt-1 text-[13px] text-fg-muted">{m.description}</p>
                  </div>
                  <span className="mt-auto flex items-center gap-1 text-[13px] font-semibold text-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    Open module <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <CommandPalette {...palette} />
    </div>
  );
}
